import { DeleteCallHistoryRecordsResponse, EndTimeSessionId, ICallHistoryRecord, UpdateMissedCallsResponse } from '@webex/component-adapter-interfaces/dist/esm/src';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DispositionTypes, LIMIT } from '../../../adapter-interfaces/src/ICallHistoryAdapter';
import { RESPONSE_STATUS } from '../../../adapter-interfaces/src/IVoicemailAdapter';
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem, safeSetLocalStorageItem } from '../../../logger/src/LocalStorage';
import { Logger, formatError, safeStringifyError } from '../../../logger/src/Logger';
import { AdapterContext } from '../contexts/AdapterContext';
import { applyOptimisticDeletion } from './callHistoryOptimisticDeletion';
import { usePhoneEnrichment } from '../../../../services/phoneEnrichment/usePhoneEnrichment';
import { normalizePhone } from '../../../../services/phoneEnrichment/normalize';
import { CONTACTS_ENRICHMENT_READY_EVENT, getContactsForEnrichment, initializeContactsForEnrichment, CONTACTS_DATA_READY_EVENT, CONTACTS_REFRESHED_EVENT, syncContactsForEnrichment } from '../../../../services/phoneEnrichment/contactsIntegration';

/**
 * Custom hook that returns call history data of the given ID.
 *
 * @param {string} userID  ID for user which to return data.
 * @returns {object} Returns {callHistory, lastUpdated} values
 */

// Legacy global key; new logic scopes cache per user to prevent cross-user leakage.
const CH_STORAGE_KEY_BASE = 'callHistoryCachedData';
const buildStorageKey = (userID?: string) => (userID ? `${CH_STORAGE_KEY_BASE}:${userID}` : CH_STORAGE_KEY_BASE);

// Purge other user caches for privacy (single-user optimization)
function purgeOtherUserCaches(currentUserID?: string) {
  try {
    if (!currentUserID || typeof window === 'undefined' || !window.localStorage) return;
    const prefix = `${CH_STORAGE_KEY_BASE}:`;
    const allKeys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k) allKeys.push(k);
    }
    allKeys.forEach(key => {
      if (key.startsWith(prefix) && key !== `${prefix}${currentUserID}`) {
        removeLocalStorageItem(key);
        try { window.localStorage.removeItem(key); } catch {}
      }
    });
  } catch (e) {
    Logger.error(`[CallHistory] purgeOtherUserCaches: failed ${(e instanceof Error) ? e.message : e}`);
  }
}
const CH_STATE_VERSION = '2';
// If initial limited fetch (LIMIT.MAX) drops by >=25% after dedup (unique <= 75% original) trigger one full fetch
const FULL_FETCH_THRESHOLD_RATIO = 0.75;
// Full refresh strategy: perform full fetch every N refreshes or after time threshold to detect server deletions
const FULL_REFRESH_EVERY_N_CALLS = 10;
const FULL_REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

type CachedCallHistoryPayload = {
  version: string;
  records: ICallHistoryRecord[];
  lastFetchedAt: string;
  lastFullRefreshAt?: string; // Track when we last did a full server sync
  refreshCount?: number; // Count of refreshes since last full
};

function getTimestamp(value?: string): number {
  if (!value) {
    return 0;
  }
  const ts = Date.parse(value);
  return Number.isNaN(ts) ? 0 : ts;
}

function sortRecordsByStartTimeDesc(records: ICallHistoryRecord[]): ICallHistoryRecord[] {
  return [...records].sort((a, b) => getTimestamp(b.startTime) - getTimestamp(a.startTime));
}

const UNKNOWN_NAME_LABELS = new Set(['unknown']);

/** True when `name` is a resolved contact label, not a raw phone number or placeholder. */
function isDisplayableContactName(name?: string, phoneNumber?: string): boolean {
  const trimmed = name?.trim();
  if (!trimmed) return false;
  if (UNKNOWN_NAME_LABELS.has(trimmed.toLowerCase())) return false;
  if (phoneNumber) {
    const normalizedName = normalizePhone(trimmed);
    const normalizedPhone = normalizePhone(phoneNumber);
    if (normalizedName && normalizedPhone && normalizedName === normalizedPhone) return false;
  }
  return true;
}

function applyMergedField(
  merged: ICallHistoryRecord & Record<string, unknown>,
  field: keyof ICallHistoryRecord,
  incomingValue: unknown,
  phoneNumber?: string
): void {
  if (field === 'name') {
    const incomingName = incomingValue as string | undefined;
    const existingName = merged.name as string | undefined;
    const resolvedPhone = (phoneNumber ?? merged.phoneNumber) as string | undefined;
    if (
      isDisplayableContactName(existingName, resolvedPhone) &&
      !isDisplayableContactName(incomingName, resolvedPhone)
    ) {
      return;
    }
  }
  merged[field as string] = incomingValue;
}

const FIELDS_TO_BACKFILL: (keyof ICallHistoryRecord)[] = [
  'name',
  'direction',
  'disposition',
  'startTime',
  'endTime',
  'sessionType',
  'phoneNumber',
  'callbackAddress',
  'durationSeconds',
  'callingSpecific',
  'lineNumber',
  'callingContainerIds',
];

type DeduplicationResult = {
  uniqueRecords: ICallHistoryRecord[];
  originalCount: number;
};

function writeCallHistoryCache(records: ICallHistoryRecord[], userID?: string, options?: { isFullRefresh?: boolean; incrementRefreshCount?: boolean }): void {
  const trimmed = records.slice(0, LIMIT.MAX);
  const key = buildStorageKey(userID);
  const existing = getLocalStorageItem<CachedCallHistoryPayload>(key);
  const existingPayload = existing && !Array.isArray(existing) ? existing : undefined;
  
  const payload: CachedCallHistoryPayload = {
    version: CH_STATE_VERSION,
    records: trimmed,
    lastFetchedAt: trimmed[0]?.startTime ?? new Date().toISOString(),
    lastFullRefreshAt: options?.isFullRefresh ? new Date().toISOString() : (existingPayload?.lastFullRefreshAt ?? new Date().toISOString()),
    refreshCount: options?.isFullRefresh ? 0 : (options?.incrementRefreshCount ? ((existingPayload?.refreshCount ?? 0) + 1) : (existingPayload?.refreshCount ?? 0)),
  };
  
  const ok = safeSetLocalStorageItem(key, payload, {
    arrayPath: 'records',
    timestampExtractor: (r: any) => Date.parse(r?.startTime || '') || 0,
    setWarningFlagKey: 'storageQuotaWarning'
  });
  if (!ok) {
    Logger.info(`[CallHistory] writeCallHistoryCache: failed to persist after safe attempts (key=${key})`);
  }
}

function readCallHistoryCache(userID?: string): CachedCallHistoryPayload | undefined {
  const key = buildStorageKey(userID);
  const raw = getLocalStorageItem<CachedCallHistoryPayload | ICallHistoryRecord[]>(key);
  // No migration: legacy global key intentionally ignored; cold start ensures fresh scoped cache.
  if (!raw) return undefined;
  if (Array.isArray(raw)) {
    const migrated = raw.slice(0, LIMIT.MAX);
    const payload: CachedCallHistoryPayload = { 
      version: CH_STATE_VERSION, 
      records: migrated, 
      lastFetchedAt: migrated[0]?.startTime ?? new Date().toISOString(),
      lastFullRefreshAt: new Date().toISOString(),
      refreshCount: 0
    };
    try { setLocalStorageItem(key, payload); } catch {}
    return payload;
  }
  const cached = raw as CachedCallHistoryPayload;
  if (cached.version !== CH_STATE_VERSION) { removeLocalStorageItem(key); return undefined; }
  return cached;
}

function shouldPerformFullRefresh(userID?: string): boolean {
  const cached = readCallHistoryCache(userID);
  if (!cached) return true;
  
  const refreshCount = cached.refreshCount ?? 0;
  const lastFullRefresh = cached.lastFullRefreshAt ? Date.parse(cached.lastFullRefreshAt) : 0;
  const timeSinceFullRefresh = Date.now() - lastFullRefresh;
  
  const needsFullByCount = refreshCount >= FULL_REFRESH_EVERY_N_CALLS;
  const needsFullByTime = timeSinceFullRefresh >= FULL_REFRESH_INTERVAL_MS;
  
  return needsFullByCount || needsFullByTime;
}

function deduplicateCallHistoryRecords(records: ICallHistoryRecord[] = []): DeduplicationResult {
  if (!records?.length) {
    Logger.info('[CallHistory] deduplicateCallHistoryRecords: No records to deduplicate');
    return {
      uniqueRecords: [],
      originalCount: records?.length ?? 0,
    };
  }

  Logger.info(`[CallHistory] deduplicateCallHistoryRecords: Processing ${records.length} records`);

  // Prefer the newest record for each session key. If any record has isRead === true,
  // the resulting record will have isRead = true. Fields from the newer record will
  // override older values, but empty/undefined incoming values will NOT overwrite
  // existing non-empty values.
  const recordMap = new Map<string, ICallHistoryRecord>();
  let duplicatesFound = 0;
  let recordsWithoutSessionId = 0;

  records.forEach((record) => {
    if (!record) return;

    const key = record.sessionId ?? record.id;
    if (!key) {
      recordsWithoutSessionId++;
      Logger.info(`[CallHistory] deduplicateCallHistoryRecords: Record missing both sessionId and id: ${JSON.stringify(record)}`);
      return;
    }

    const existing = recordMap.get(key);
    if (!existing) {
      recordMap.set(key, { ...record });
      return;
    }

    // Found a duplicate
    duplicatesFound++;
    const existingTs = getTimestamp(existing.startTime);
    const recordTs = getTimestamp(record.startTime);

    // Commenting this out to reduce log noise
    //Logger.info(`[CallHistory] deduplicateCallHistoryRecords: Duplicate found for sessionId=${key}, existingTs=${existingTs}, recordTs=${recordTs}, keeping ${recordTs >= existingTs ? 'newer' : 'existing'}`);

    // Start with a shallow clone of the existing record
    const merged: ICallHistoryRecord & Record<string, unknown> = { ...existing };

    // If incoming record is newer or same time, prefer its non-empty values
    if (recordTs >= existingTs) {
      FIELDS_TO_BACKFILL.forEach((field) => {
        const incomingValue = (record as unknown as Record<string, unknown>)[field as string];
        if (incomingValue !== undefined && incomingValue !== null && incomingValue !== '') {
          applyMergedField(merged, field, incomingValue, record.phoneNumber);
        }
      });
    } else {
      // Existing is newer: only backfill missing fields from the older incoming record
      FIELDS_TO_BACKFILL.forEach((field) => {
        const currentValue = merged[field as string];
        if (currentValue === undefined || currentValue === null || currentValue === '') {
          const incomingValue = (record as unknown as Record<string, unknown>)[field as string];
          if (incomingValue !== undefined && incomingValue !== null && incomingValue !== '') {
            applyMergedField(merged, field, incomingValue, record.phoneNumber);
          }
        }
      });
    }

    // isRead should be true if any record reports it as read
    const wasReadMerged = !!existing.isRead || !!record.isRead;
    if (wasReadMerged && !existing.isRead) {
      Logger.info(`[CallHistory] deduplicateCallHistoryRecords: Merged isRead=true from duplicate for sessionId=${key}`);
    }
    merged.isRead = wasReadMerged;

    recordMap.set(key, merged as ICallHistoryRecord);
  });

  const uniqueCount = recordMap.size;
  const deduplicationRate = records.length > 0 ? ((records.length - uniqueCount) / records.length * 100).toFixed(1) : '0';
  
  Logger.info(`[CallHistory] deduplicateCallHistoryRecords: Complete - original=${records.length}, unique=${uniqueCount}, duplicates=${duplicatesFound}, recordsWithoutSessionId=${recordsWithoutSessionId}, deduplicationRate=${deduplicationRate}%`);

  return {
    uniqueRecords: Array.from(recordMap.values()),
    originalCount: records.length,
  };
}

// Removed cooldown persistence; simplified logic only needs in-memory newest timestamp

export default function useCallHistory(userID?: string) {
  const [callHistory, setCallHistory] = useState<ICallHistoryRecord[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date>();
  const [loading, setLoading] = useState<boolean>(true);
  const [contactsRefreshTick, setContactsRefreshTick] = useState<number>(0);
  const ctx = useContext(AdapterContext);
  const isInitialLoadRef = useRef(true);
  const performedFullFetchRef = useRef(false); // ensures single full fetch trigger
  const callHistoryRef = useRef<ICallHistoryRecord[]>([]);
  const newestTimestampRef = useRef<number | null>(null);
  const pendingFullRefreshRef = useRef(false); // Track if next adapter update should be treated as full refresh

  useEffect(() => {
    // These refs and the rendered list are user-scoped. Reset them before
    // cache hydration/subscription so an account switch cannot merge or
    // briefly render the previous account's call history.
    isInitialLoadRef.current = true;
    performedFullFetchRef.current = false;
    callHistoryRef.current = [];
    newestTimestampRef.current = null;
    pendingFullRefreshRef.current = false;
    setCallHistory([]);
    setLastUpdated(undefined);
    setLoading(Boolean(userID));
  }, [userID]);

  const onError = useCallback((error: unknown) => {
    console.error('error', safeStringifyError(error));
    setLoading(false);
  }, []);

  const mergeAndUpdateState = useCallback((incoming: ICallHistoryRecord[] = [], options: { isFullFetch: boolean; isInitialFetch: boolean; isFullRefresh?: boolean }) => {
    const { isFullFetch, isInitialFetch, isFullRefresh } = options;
    const cached = readCallHistoryCache(userID);
    const existing = callHistoryRef.current.length ? callHistoryRef.current : cached?.records ?? [];
    const { uniqueRecords: incomingUnique, originalCount: incomingOriginalCount } = deduplicateCallHistoryRecords(incoming);
    const incomingUniqueCount = incomingUnique.length;
    
    // FULL REFRESH MODE: Replace existing data entirely (detects server deletions)
    if (isFullRefresh) {
      const sorted = sortRecordsByStartTimeDesc(incomingUnique);
      const trimmed = sorted.slice(0, LIMIT.MAX);
      writeCallHistoryCache(trimmed, userID, { isFullRefresh: true });
      callHistoryRef.current = trimmed;
      setCallHistory(trimmed);
      setLastUpdated(new Date());
      setLoading(false);
      return;
    }
    
    // Overwrite guard: if we have existing > incomingUnique and this is an initial limited fetch (< LIMIT.MAX original) skip replacement
    const isInitialSmallFetch = isInitialFetch && incomingOriginalCount > 0 && incomingOriginalCount < LIMIT.MAX;
    if (isInitialSmallFetch && existing.length > incomingUniqueCount) {
      Logger.info(`[CallHistory] mergeAndUpdateState: Ignoring smaller initial fetch (incomingUnique=${incomingUniqueCount}, existing=${existing.length})`);
      setLoading(false);
      return;
    }
    
    // INCREMENTAL MODE: Merge with existing (normal refresh)
    const mergedSource = [...incomingUnique, ...existing];
    const { uniqueRecords: mergedUnique } = deduplicateCallHistoryRecords(mergedSource);
    const sorted = sortRecordsByStartTimeDesc(mergedUnique);
    const trimmed = sorted.slice(0, LIMIT.MAX);
    writeCallHistoryCache(trimmed, userID, { incrementRefreshCount: !isInitialFetch });
    callHistoryRef.current = trimmed;
    setCallHistory(trimmed);
    setLastUpdated(new Date());
    setLoading(false);
  }, [userID]);

  // Handle adapter emissions: deduplicate, maybe trigger full-fetch escalation, update newest timestamp
  const handleAdapterUpdate = useCallback((incoming: ICallHistoryRecord[] = []) => {
    const isInitial = isInitialLoadRef.current;
    const isFullRefresh = pendingFullRefreshRef.current;
    
    if (isInitial) {
      isInitialLoadRef.current = false;
    }
    
    if (isFullRefresh) {
      pendingFullRefreshRef.current = false;
      mergeAndUpdateState(incoming, { isFullFetch: false, isInitialFetch: false, isFullRefresh: true });
      if (incoming?.length) {
        const sorted = sortRecordsByStartTimeDesc(incoming);
        const newest = sorted[0]?.startTime;
        if (newest) {
          const ts = getTimestamp(newest);
          if (ts > 0) newestTimestampRef.current = ts;
        }
      }
      return;
    }

    // On first limited batch, evaluate dedup ratio to decide a one-time full fetch
    if (isInitial && incoming.length === LIMIT.MAX && !performedFullFetchRef.current) {
      const { uniqueRecords, originalCount } = deduplicateCallHistoryRecords(incoming);
      const ratio = uniqueRecords.length / (originalCount || 1);
      if (ratio <= FULL_FETCH_THRESHOLD_RATIO) {
        Logger.info(`[CallHistory] handleAdapterUpdate: ratio ${ratio.toFixed(3)} <= ${FULL_FETCH_THRESHOLD_RATIO}; triggering full fetch`);
        performedFullFetchRef.current = true;
        try {
          (ctx?.callHistoryAdapter as any)?.fetchCallHistoryWithoutLimit?.(userID);
        } catch (e) {
          Logger.error(`[CallHistory] handleAdapterUpdate: full fetch trigger failed: ${e instanceof Error ? e.message : e}`);
        }
      }
      mergeAndUpdateState(incoming, { isFullFetch: false, isInitialFetch: true, isFullRefresh: false });
      if (uniqueRecords[0]?.startTime) {
        const ts = getTimestamp(uniqueRecords[0].startTime);
        if (ts > 0) newestTimestampRef.current = ts;
      }
      return;
    }

    mergeAndUpdateState(incoming, { isFullFetch: false, isInitialFetch: isInitial, isFullRefresh: false });

    // Update newest timestamp ref opportunistically from incoming batch
    if (incoming?.length) {
      const sorted = sortRecordsByStartTimeDesc(incoming);
      const newest = sorted[0]?.startTime;
      if (newest) {
        const ts = getTimestamp(newest);
        if (ts > 0 && (!newestTimestampRef.current || ts > newestTimestampRef.current)) {
          newestTimestampRef.current = ts;
        }
      }
    }
  }, [mergeAndUpdateState, ctx?.callHistoryAdapter, userID]);

  const refresh = useCallback(() => {
    if (!ctx?.callHistoryAdapter?.refresh) return;
    
    const needsFullRefresh = shouldPerformFullRefresh(userID);
    
    if (needsFullRefresh) {
      pendingFullRefreshRef.current = true;
      (ctx.callHistoryAdapter as any).refresh(userID, undefined);
      void syncContactsForEnrichment();
      return;
    }
    
    // Incremental refresh: use 'since' optimization
    const now = Date.now();
    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
    const sinceTs = newestTimestampRef.current ? Math.max(newestTimestampRef.current - 1, now - THIRTY_DAYS_MS) : undefined;
    const options = sinceTs ? { since: new Date(sinceTs).toISOString() } : undefined;
    (ctx.callHistoryAdapter as any).refresh(userID, options);
    void syncContactsForEnrichment();
  }, [userID, ctx?.callHistoryAdapter]);

  const updateMissedCalls = useCallback(() => {
    // Extract sessionId and endTime from call history for calls that are not read and have a disposition of "Missed"
    const endTimeSessionIds: EndTimeSessionId[] = callHistory
      .filter((record) => !record.isRead && (record.disposition === DispositionTypes.MISSED))
      .map((record) => {
        const sessionId = record.sessionId ?? record.id;
        const endTime = record.endTime;
        if (!sessionId || !endTime) {
          return undefined;
        }
        return {
          endTime,
          sessionId,
        } as EndTimeSessionId;
      })
      .filter((value): value is EndTimeSessionId => Boolean(value));
    // Send the data to the adapter layer
    if (ctx?.callHistoryAdapter?.updateMissedCalls && endTimeSessionIds.length > 0) {
      Logger.info(
        `WebexCallHistory.updateMissedCalls(): count of missed calls : ${endTimeSessionIds.length}`
      );
      const subscription = ctx.callHistoryAdapter.updateMissedCalls(endTimeSessionIds).subscribe((res: UpdateMissedCallsResponse) => {
        if (res?.message === RESPONSE_STATUS.SUCCESS) {
          Logger.info(
            `WebexCallHistory.updateMissedCalls(): got status code with message: ${res?.statusCode} - ${res?.message}`
          );
          Logger.info(
            `WebexCallHistory.updateMissedCalls(): ${res?.data?.readStatusMessage}`
          );
          return;
        } else if (res.message === RESPONSE_STATUS.FAILURE) {
          Logger.info(
            `WebexCallHistory.updateMissedCalls(): got status code with message: ${res?.statusCode} - ${res?.message}`
          );
          Logger.error(
            `WebexCallHistory.updateMissedCalls(): got error message: '${formatError(
              res?.data?.error
            )}'.`
          );
          return;
        }
      })
      return () => {
        subscription?.unsubscribe();
      };
      // Assuming you have the webex_access_token available in your conte
      //return ctx.callHistoryAdapter.updateMissedCalls(endTimeSessionIds);
    } else {
      Logger.info(
        `WebexCallHistory.updateMissedCalls(): count of missed calls : ${endTimeSessionIds.length}`
      );
    }
  }, [callHistory, ctx?.callHistoryAdapter]);

  const deleteSingleCallHistoryRecord = useCallback((id: string, endTime: string) => {
    const record = callHistoryRef.current.find((item) => item.id === id);
    const sessionId = record?.sessionId ?? id;
    const resolvedEndTime = record?.endTime ?? endTime;

    const deleteSessionIds = sessionId && resolvedEndTime ? [
      {
        endTime: resolvedEndTime,
        sessionId,
      }
    ] : [];

    // Optimistic removal - always enabled for all users
    const previousRecords = [...callHistoryRef.current];
    let optimisticRemovedCount = 0;
    if (deleteSessionIds.length > 0) {
      const { nextRecords, removedCount } = applyOptimisticDeletion({
        records: callHistoryRef.current,
        targetId: id,
        targetSessionId: sessionId,
      });
      optimisticRemovedCount = removedCount;
      if (removedCount > 0) {
        callHistoryRef.current = nextRecords;
        setCallHistory(nextRecords);
        setLastUpdated(new Date());
        setLoading(false);
        if (nextRecords.length > 0) {
          writeCallHistoryCache(nextRecords, userID, { incrementRefreshCount: false });
        } else {
          removeLocalStorageItem(buildStorageKey(userID));
        }
      }
    }

    // Send the data to the adapter layer
    if (ctx?.callHistoryAdapter?.deleteCallHistoryRecords && deleteSessionIds.length > 0) {
      const subscription = ctx.callHistoryAdapter.deleteCallHistoryRecords(deleteSessionIds).subscribe((res: DeleteCallHistoryRecordsResponse) => {
        if (res?.message === RESPONSE_STATUS.SUCCESS) {
          Logger.info(
            `WebexCallHistory.deleteCallHistoryRecords(): got status code with message: ${res?.statusCode} - ${res?.message}`
          );
          Logger.info(
            `WebexCallHistory.deleteCallHistoryRecords(): ${res?.data?.deleteStatusMessage}`
          );
          return;
        } else if (res.message === RESPONSE_STATUS.FAILURE) {
          Logger.info(
            `WebexCallHistory.deleteCallHistoryRecords(): got status code with message: ${res?.statusCode} - ${res?.message}`
          );
          Logger.error(
            `WebexCallHistory.deleteCallHistoryRecords(): got error message: '${formatError(
              res?.data?.error
            )}'.`
          );
          if (optimisticRemovedCount > 0) {
            // Roll back optimistic change on failure
            callHistoryRef.current = previousRecords;
            setCallHistory(previousRecords);
            if (previousRecords.length > 0) {
              writeCallHistoryCache(previousRecords, userID, { incrementRefreshCount: false });
            } else {
              removeLocalStorageItem(buildStorageKey(userID));
            }
          }
          return;
        }
      })
      return () => {
        subscription?.unsubscribe();
      };
    } else {
      Logger.info(
        `WebexCallHistory.deleteCallHistoryRecords(): count of delete calls : ${deleteSessionIds.length}`
      );
    }
  }, [ctx?.callHistoryAdapter, userID]);

  const enableEnrichment = useMemo(
    () => Boolean(ctx?.dss && userID),
    [ctx?.dss, userID]
  );

  // Filter and map call history to CallHistoryItem format for enrichment
  const callHistoryForEnrichment = useMemo(
    () => callHistory
      .filter((item) => Boolean(item.phoneNumber))
      .map((item) => ({
        ...item,
        phoneNumber: item.phoneNumber!,
      })),
    [callHistory]
  );

  const contactsForEnrichment = useMemo(
    () => getContactsForEnrichment() || undefined,
    [contactsRefreshTick]
  );

  const { enrichedItems } = usePhoneEnrichment(callHistoryForEnrichment, {
    enableEnrichment,
    userId: userID || '',
    dss: ctx?.dss as any,
    unknownLabel: 'Unknown',
    contacts: contactsForEnrichment,
    waitForContacts: enableEnrichment,
    contactsRefreshSignal: contactsRefreshTick,
  });

  const enrichedCallHistory = useMemo(
    () => {
      if (!enableEnrichment) {
        return callHistory;
      }
      
      // Create a map of enriched items by id for quick lookup
      const enrichedMap = new Map(
        (enrichedItems as ICallHistoryRecord[]).map((item) => [item.id, item])
      );
      
      // Merge enriched data back into the original call history
      return callHistory.map((item) => {
        const enrichedItem = enrichedMap.get(item.id);
        if (enrichedItem && (enrichedItem as any)?.enrichment?.displayName) {
          return { ...item, name: (enrichedItem as any).enrichment.displayName };
        }
        return item;
      });
    },
    [enableEnrichment, enrichedItems, callHistory]
  );

  useEffect(() => {
    purgeOtherUserCaches(userID);
    const cached = readCallHistoryCache(userID);
    const existingNewest = cached?.records?.[0]?.startTime;
    if (existingNewest) {
      const ts = getTimestamp(existingNewest);
      if (ts > 0) newestTimestampRef.current = ts;
    }
    const subscription = (ctx?.callHistoryAdapter as any)?.getAll(userID)?.subscribe({ next: handleAdapterUpdate, error: onError });
    return () => subscription?.unsubscribe();
  }, [ctx?.callHistoryAdapter, handleAdapterUpdate, onError, userID]);

  useEffect(() => {
    const cached = readCallHistoryCache(userID);
    const records = cached?.records ?? [];
    if (!records.length) return;
    const { uniqueRecords } = deduplicateCallHistoryRecords(records);
    const trimmed = uniqueRecords.slice(0, LIMIT.MAX);
    callHistoryRef.current = trimmed;
    setCallHistory(trimmed);
    setLastUpdated(new Date(cached?.lastFetchedAt ?? new Date().toISOString()));
    if (trimmed[0]?.startTime) newestTimestampRef.current = getTimestamp(trimmed[0].startTime);
    setLoading(false);
  }, [userID]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => {
      setContactsRefreshTick((tick) => tick + 1);
    };
    window.addEventListener(CONTACTS_ENRICHMENT_READY_EVENT, handler as EventListener);
    window.addEventListener(CONTACTS_DATA_READY_EVENT, handler as EventListener);
    window.addEventListener(CONTACTS_REFRESHED_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(CONTACTS_ENRICHMENT_READY_EVENT, handler as EventListener);
      window.removeEventListener(CONTACTS_DATA_READY_EVENT, handler as EventListener);
      window.removeEventListener(CONTACTS_REFRESHED_EVENT, handler as EventListener);
    };
  }, [userID]);

  useEffect(() => {
    if (!enableEnrichment || !userID) return;
    if (contactsForEnrichment) return;
    void initializeContactsForEnrichment((window as any)?.webex, userID).catch((error: unknown) => {
      Logger.error(`[Enrichment] CallHistory: contacts init failed: ${error instanceof Error ? error.message : error}`);
    });
  }, [enableEnrichment, userID, contactsForEnrichment]);

  return {
    lastUpdated,
    callHistory: enrichedCallHistory,
    refresh,
    loading,
    updateMissedCalls,
    deleteSingleCallHistoryRecord
  } as const;
}

// Test-only exports (non-production). These help unit tests validate internal behaviors
// without relying on brittle re-implementation. They are tree-shaken from prod builds.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const __TEST_ONLY__ = {
  // Expose constants / helpers for targeted scenario tests (bench plan S4-S14 etc.)
  // NOTE: Functions that are not referenced retain minification eligibility.
  // We intentionally do not export writeCallHistoryCache to avoid unintended writes.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _deduplicate: (records: any[]) => (deduplicateCallHistoryRecords as any)(records),
  _sortByStartDesc: (records: any[]) => sortRecordsByStartTimeDesc(records as any),
  isDisplayableContactName,
};
